
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradeHistoryIdsParams struct
 */
class TradeHistoryIdsParams {
  /**
   * Initialize struct
   * @param {number=} lastRecordId
   * @param {number=} maxRecordId
   * @param {!number=} pageSize
   * @param {!boolean=} readFromArchive
   * @param {!boolean=} useSnapshot
   * @constructor
   */
  constructor (lastRecordId = null, maxRecordId = null, pageSize = 1000, readFromArchive = false, useSnapshot = false) {
    this.LastRecordId = lastRecordId
    this.MaxRecordId = maxRecordId
    this.PageSize = pageSize
    this.ReadFromArchive = readFromArchive
    this.UseSnapshot = useSnapshot
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradeHistoryIdsParams}
   * @param {!TradeHistoryIdsParams} other Other struct
   * @returns {!TradeHistoryIdsParams} This struct
   */
  copy (other) {
    if (other.LastRecordId != null) {
      this.LastRecordId = UInt64.fromNumber(other.LastRecordId)
    } else {
      this.LastRecordId = null
    }
    if (other.MaxRecordId != null) {
      this.MaxRecordId = UInt64.fromNumber(other.MaxRecordId)
    } else {
      this.MaxRecordId = null
    }
    if (other.PageSize != null) {
      this.PageSize = other.PageSize
    } else {
      this.PageSize = null
    }
    if (other.ReadFromArchive != null) {
      this.ReadFromArchive = other.ReadFromArchive
    } else {
      this.ReadFromArchive = null
    }
    if (other.UseSnapshot != null) {
      this.UseSnapshot = other.UseSnapshot
    } else {
      this.UseSnapshot = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradeHistoryIdsParams}
   * @returns {!TradeHistoryIdsParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradeHistoryIdsParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradeHistoryIdsParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradeHistoryIdsParams}
   * @param {!TradeHistoryIdsParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeHistoryIdsParams)) {
      throw new TypeError('Instance of TradeHistoryIdsParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradeHistoryIdsParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      LastRecordId: ((this.LastRecordId != null) ? this.LastRecordId.toNumber() : null),
      MaxRecordId: ((this.MaxRecordId != null) ? this.MaxRecordId.toNumber() : null),
      PageSize: ((this.PageSize != null) ? this.PageSize : null),
      ReadFromArchive: ((this.ReadFromArchive != null) ? this.ReadFromArchive : null),
      UseSnapshot: ((this.UseSnapshot != null) ? this.UseSnapshot : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradeHistoryIdsParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradeHistoryIdsParams} other Object value
   * @returns {!TradeHistoryIdsParams} Created struct
   */
  static fromObject (other) {
    return new TradeHistoryIdsParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradeHistoryIdsParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradeHistoryIdsParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradeHistoryIdsParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10053
  }
}

export { TradeHistoryIdsParams };

/**
 * Fast Binary Encoding TradeHistoryIdsParams field model
 */
class FieldModelTradeHistoryIdsParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.LastRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, 4 + 4), buffer, 4 + 4)
    this.MaxRecordId = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize), buffer, this.LastRecordId.fbeOffset + this.LastRecordId.fbeSize)
    this.PageSize = new fbe.FieldModelUInt32(buffer, this.MaxRecordId.fbeOffset + this.MaxRecordId.fbeSize)
    this.ReadFromArchive = new fbe.FieldModelBool(buffer, this.PageSize.fbeOffset + this.PageSize.fbeSize)
    this.UseSnapshot = new fbe.FieldModelBool(buffer, this.ReadFromArchive.fbeOffset + this.ReadFromArchive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.LastRecordId.fbeSize + this.MaxRecordId.fbeSize + this.PageSize.fbeSize + this.ReadFromArchive.fbeSize + this.UseSnapshot.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.LastRecordId.fbeExtra + this.MaxRecordId.fbeExtra + this.PageSize.fbeExtra + this.ReadFromArchive.fbeExtra + this.UseSnapshot.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradeHistoryIdsParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10053
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LastRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.MaxRecordId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MaxRecordId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MaxRecordId.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageSize.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.ReadFromArchive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ReadFromArchive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReadFromArchive.fbeSize

    if ((fbeCurrentSize + this.UseSnapshot.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSnapshot.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSnapshot.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!TradeHistoryIdsParams} fbeValue Default value, defaults is new TradeHistoryIdsParams()
   * @returns {!TradeHistoryIdsParams} TradeHistoryIdsParams value
   */
  get (fbeValue = new TradeHistoryIdsParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!TradeHistoryIdsParams} fbeValue TradeHistoryIdsParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LastRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.LastRecordId = this.LastRecordId.get()
    } else {
      fbeValue.LastRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LastRecordId.fbeSize

    if ((fbeCurrentSize + this.MaxRecordId.fbeSize) <= fbeStructSize) {
      fbeValue.MaxRecordId = this.MaxRecordId.get()
    } else {
      fbeValue.MaxRecordId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MaxRecordId.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) <= fbeStructSize) {
      fbeValue.PageSize = this.PageSize.get(1000)
    } else {
      fbeValue.PageSize = 1000
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.ReadFromArchive.fbeSize) <= fbeStructSize) {
      fbeValue.ReadFromArchive = this.ReadFromArchive.get(false)
    } else {
      fbeValue.ReadFromArchive = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ReadFromArchive.fbeSize

    if ((fbeCurrentSize + this.UseSnapshot.fbeSize) <= fbeStructSize) {
      fbeValue.UseSnapshot = this.UseSnapshot.get(false)
    } else {
      fbeValue.UseSnapshot = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSnapshot.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradeHistoryIdsParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {!TradeHistoryIdsParams} fbeValue TradeHistoryIdsParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradeHistoryIdsParams}
   * @param {TradeHistoryIdsParams} fbeValue TradeHistoryIdsParams value
   */
  setFields (fbeValue) {
    this.LastRecordId.set(fbeValue.LastRecordId)
    this.MaxRecordId.set(fbeValue.MaxRecordId)
    this.PageSize.set(fbeValue.PageSize)
    this.ReadFromArchive.set(fbeValue.ReadFromArchive)
    this.UseSnapshot.set(fbeValue.UseSnapshot)
  }
}

export { FieldModelTradeHistoryIdsParams };

/**
 * Fast Binary Encoding TradeHistoryIdsParams model
 */
class TradeHistoryIdsParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradeHistoryIdsParams(this.buffer, 4)
  }

  /**
   * Get the TradeHistoryIdsParams model
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!FieldModelTradeHistoryIdsParams} model TradeHistoryIdsParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradeHistoryIdsParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradeHistoryIdsParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradeHistoryIdsParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradeHistoryIdsParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradeHistoryIdsParamsModel}
   * @param {!TradeHistoryIdsParams} value TradeHistoryIdsParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradeHistoryIdsParamsModel}
   * @param {!TradeHistoryIdsParams} value TradeHistoryIdsParams value, defaults is new TradeHistoryIdsParams()
   * @return {!object} Deserialized TradeHistoryIdsParams value and its size
   */
  deserialize (value = new TradeHistoryIdsParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradeHistoryIdsParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradeHistoryIdsParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradeHistoryIdsParamsModel}
   * @param {!number} prev Previous TradeHistoryIdsParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradeHistoryIdsParamsModel };
